import React from 'react';
import { ArrowRight, ArrowDown, ArrowLeftRight, ArrowUpDown } from 'lucide-react';

// Esquemas sobrios (nov-2026, diseño B2B): cajas rectas con filete fino, una sola
// caja destacada con filete azul marino, flechas grises y textos cortos. En móvil
// se apilan en vertical. Jesús: «no me niego a esquemas si aportan»; úsense solo
// donde expliquen algo que el texto cuenta peor (arquitecturas, flujos).

const NAVY = '#0b1d39';

const Box = ({ title, text, items, strong = false }) => (
  <div
    className={`h-full bg-white px-4 py-4 ${strong ? 'border-2' : 'border border-slate-300'}`}
    style={strong ? { borderColor: NAVY } : undefined}
  >
    <p className={`text-[15px] font-semibold leading-snug ${strong ? 'text-slate-900' : 'text-slate-900'}`}>{title}</p>
    {text && <p className="mt-1.5 text-[13px] leading-5 text-slate-600">{text}</p>}
    {items && (
      <ul className="mt-2 space-y-1">
        {items.map((it) => (
          <li key={it} className="text-[13px] leading-5 text-slate-600">{it}</li>
        ))}
      </ul>
    )}
  </div>
);

const Arrow = ({ both = false }) => {
  const H = both ? ArrowLeftRight : ArrowRight;
  const V = both ? ArrowUpDown : ArrowDown;
  return (
    <div className="flex items-center justify-center py-1 md:py-0 md:px-1.5 text-slate-400" aria-hidden="true">
      <V className="w-5 h-5 md:hidden" strokeWidth={1.5} />
      <H className="hidden md:block w-5 h-5" strokeWidth={1.5} />
    </div>
  );
};

/**
 * Cadena de pasos en horizontal (vertical en móvil).
 * steps = [{ title, text?, items?, strong? }]; both: flechas de ida y vuelta.
 */
export const FlowDiagram = ({ steps, caption, both = false, label }) => (
  <figure className="py-2" aria-label={label}>
    <div className="flex flex-col md:flex-row md:items-stretch">
      {steps.map((s, i) => (
        <React.Fragment key={s.title}>
          {i > 0 && <Arrow both={both} />}
          <div className="md:flex-1 min-w-0">
            <Box {...s} />
          </div>
        </React.Fragment>
      ))}
    </div>
    {caption && <figcaption className="mt-4 text-sm leading-6 text-slate-500">{caption}</figcaption>}
  </figure>
);

/**
 * Esquema de tres columnas con un centro destacado: lo que entra, la pieza
 * central y lo que sale. left/right = { title, items: [{ title, text }] };
 * center = { title, text, items }.
 */
export const HubDiagram = ({ left, center, right, caption, label }) => (
  <figure className="py-2" aria-label={label}>
    <div className="grid md:grid-cols-[1fr_auto_1fr_auto_1fr] items-stretch">
      <div className="min-w-0">
        <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500 mb-3">{left.title}</p>
        <div className="space-y-2">
          {left.items.map((it) => <Box key={it.title} title={it.title} text={it.text} />)}
        </div>
      </div>
      <Arrow both />
      <div className="min-w-0 flex flex-col">
        <p className="hidden md:block md:invisible text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500 mb-3" aria-hidden="true">{center.title}</p>
        <div className="flex-1">
          <Box title={center.title} text={center.text} items={center.items} strong />
        </div>
      </div>
      <Arrow both />
      <div className="min-w-0">
        <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500 mb-3">{right.title}</p>
        <div className="space-y-2">
          {right.items.map((it) => <Box key={it.title} title={it.title} text={it.text} />)}
        </div>
      </div>
    </div>
    {caption && <figcaption className="mt-4 text-sm leading-6 text-slate-500">{caption}</figcaption>}
  </figure>
);
